import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PARAGRAPHS = [
  {
    bold: true,
    text: "At Eurosteel, we are a New Zealand based supplier of steel windows and doors, inspired by European design.",
  },
  {
    bold: false,
    text: "We pride ourselves on our commitment to quality, craftsmanship and using only the highest quality sourced steel to create products that are both affordable and elegant.",
  },
];

// Flatten into a single word list so the scrub is one continuous timeline
const WORDS = PARAGRAPHS.flatMap((p, pi) =>
  p.text.split(" ").map((word, wi, arr) => ({
    word,
    bold: p.bold,
    isParaEnd: wi === arr.length - 1,
    paraIndex: pi,
  }))
);

const Word = ({ item, index, total, progress }) => {
  const start = index / total;
  const end = (index + 1) / total;
  const color = useTransform(progress, [start, end], ["#0a0a0c", "#c6a87c"]);
  return (
    <motion.span
      style={{ color, fontWeight: item.bold ? 600 : 300 }}
      className="inline-block"
    >
      {item.word}
      {index < total - 1 ? "\u00A0" : ""}
    </motion.span>
  );
};

export const IntroStatement = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "end 0.9"],
  });

  // Split the flattened word list back into paragraph chunks while keeping global indices
  let globalIndex = 0;
  const paragraphs = PARAGRAPHS.map((p) => {
    const wordsInPara = p.text.split(" ");
    const items = wordsInPara.map((_, wi) => {
      const item = WORDS[globalIndex];
      const idx = globalIndex;
      globalIndex += 1;
      return { item, idx };
    });
    return { bold: p.bold, items };
  });

  return (
    <section
      id="intro-statement"
      ref={ref}
      data-testid="intro-statement"
      className="relative bg-[#f5f3ee] px-6 pt-16 pb-32 lg:px-16 lg:pt-24 lg:pb-44"
    >
      <div className="mx-auto max-w-[1200px] space-y-8 lg:space-y-12">
        {paragraphs.map((p, pi) => (
          <p
            key={`para-${pi}`}
            data-testid={`intro-statement-paragraph-${pi + 1}`}
            className={`font-serif-display text-xl leading-[1.3] tracking-[-0.005em] sm:text-2xl md:text-[28px] lg:text-[34px] lg:leading-[1.25] ${
              pi === 1 ? "ml-auto max-w-[860px] lg:pl-24 xl:pl-40 text-left" : "max-w-[1000px]"
            }`}
          >
            {p.items.map(({ item, idx }) => (
              <Word
                key={idx}
                item={item}
                index={idx}
                total={WORDS.length}
                progress={scrollYProgress}
              />
            ))}
          </p>
        ))}
      </div>

      {/* Featured-on badge — lower-left credential */}
      <motion.a
        href="https://archipro.co.nz/professional/eurosteel"
        target="_blank"
        rel="noreferrer"
        data-testid="archipro-badge"
        aria-label="Featured on ArchiPro — view Eurosteel on ArchiPro"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
        className="group absolute bottom-8 left-6 inline-block lg:bottom-12 lg:left-16"
      >
        <img
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_c8c33088-e275-4c7a-be4e-9570c082ca94/artifacts/uj2c8p4t_ap-badge-black2.webp"
          alt="Featured on ArchiPro"
          data-testid="archipro-badge-image"
          className="h-14 w-auto rounded-md shadow-lg ring-1 ring-black/10 transition-transform duration-500 group-hover:-translate-y-[2px] lg:h-16"
        />
      </motion.a>
    </section>
  );
};
